import { useState } from 'react';
import Select from 'react-select-2';
import type { VoidFunctionComponent } from 'react';
import type { ValueType } from 'react-select-2/lib/types';

import { BannerStyle } from '../../../../typings/enums';
import { useDialogContext } from '../../../contexts/DialogContext';
import { useAsyncStatus } from '../../../hooks';
import { post } from '../../../redux/api';
import { selectOptionFactory } from '../../../utils';
import { InlineLoaderButton } from '../../generic';
import Banner from '../../generic/Banner';
import TextArea from '../../generic/Forms/TextArea';
import type { FailedFetchResponse } from '../../../helpers/errors';

function isConflictResponse(res: any) {
  return res?.status === 409;
}

const concurrencyOptions = [2, 4, 8, 16, 32];

const CVEVulnTransformComponent: VoidFunctionComponent = () => {
  const { onConfirm } = useDialogContext();
  const [cveText, setCveText] = useState('');
  const [concurrency, setConcurrency] = useState<number>(16);
  const [isShowingConflictAlert, setIsShowingConflictAlert] = useState(false);
  const { status, onPending, onSuccess, onError } = useAsyncStatus();

  const submitTransform = async () => {
    onPending();
    try {
      const cves = cveText.split('\n');
      await post(`/api/vulns/transform`, { cves, concurrency });
      setIsShowingConflictAlert(false);
      onSuccess();
    } catch (err) {
      if (isConflictResponse(err)) {
        setIsShowingConflictAlert(true);
        onSuccess();
        return;
      }

      onError(err as FailedFetchResponse);
    }
  };

  return (
    <div className="card">
      <h3 className="card-title padding-bottom-sm">
        Transform Vulns from Source
      </h3>
      {isShowingConflictAlert && (
        <div className="padding-bottom-md">
          <Banner
            isDismissible
            onDismiss={() => setIsShowingConflictAlert(false)}
            title="Failed"
            body="A transform job already exists for these CVEs. Try again later."
            bannerStyle={BannerStyle.Medium}
          />
        </div>
      )}
      {status.error && (
        <div className="padding-bottom-md">
          <Banner
            isDismissible
            onDismiss={() => setIsShowingConflictAlert(false)}
            title="Failed"
            body={status.error}
            bannerStyle={BannerStyle.High}
          />
        </div>
      )}
      <div>
        Line-Separated CVE List:
      </div>
      <TextArea
        label="Line-Separated CVE List"
        value={cveText}
        placeholder="CVE-123-4567&#10;CVE-111-1111"
        onChange={(val) => setCveText(val)}
        isResizable />
      <div className="width-full">
        <div>
          Concurrency:
        </div>
        <Select<{ label: string; value: number }>
          value={selectOptionFactory<number>(concurrency)}
          options={concurrencyOptions.map((f) => selectOptionFactory(f))}
          onChange={(option: ValueType<{ label: string; value: number }>) => {
            setConcurrency(option?.value ? Number(option.value) : 16);
          }}
        />
      </div>
      <div className="padding-top-sm">
        <InlineLoaderButton
          onClick={() => {
            onConfirm({
              title: 'Are you sure?',
              body: 'Vulnerability transform can be a long running process, depending on how many vulns exist for these CVEs. Are you sure you want to proceed?',
              onConfirm: submitTransform,
            });
          }}
          isLoading={status.loading}
          isDisabled={status.loading}
        >
          Submit
        </InlineLoaderButton>
      </div>
    </div>
  );
};

export default CVEVulnTransformComponent;
